import { useState } from 'react';
import type { RegentsMCQuestion } from '../data/geometryRegentsNY';

interface PartIMCQProps {
  questions: RegentsMCQuestion[];
  submitted: boolean;
  selectedAnswers: Record<number, number>;
  onSelect: (qIndex: number, optIndex: number) => void;
}

export default function PartIMCQ({ questions, submitted, selectedAnswers, onSelect }: PartIMCQProps) {
  const [showSolutions, setShowSolutions] = useState<Record<number, boolean>>({});

  const toggleSolution = (id: number) => {
    setShowSolutions(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="space-y-5">
      {questions.map((q, idx) => {
        const selected = selectedAnswers[q.id];
        const isCorrect = submitted && selected === q.correctAnswer;
        const isWrong = submitted && selected !== undefined && selected !== q.correctAnswer;
        const showSol = showSolutions[q.id];

        return (
          <div
            key={q.id}
            className={`bg-white rounded-2xl shadow-md border-2 transition-all ${
              isCorrect ? 'border-emerald-300' : isWrong ? 'border-red-300' : 'border-gray-100'
            }`}
          >
            <div className={`px-6 py-4 rounded-t-2xl flex items-start gap-4 ${
              isCorrect ? 'bg-emerald-50' : isWrong ? 'bg-red-50' : 'bg-gray-50'
            }`}>
              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold flex-shrink-0 ${
                isCorrect ? 'bg-emerald-500 text-white' : isWrong ? 'bg-red-500 text-white' : 'bg-teal-600 text-white'
              }`}>
                {isCorrect ? <i className="ri-check-line" /> : isWrong ? <i className="ri-close-line" /> : idx + 1}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs font-semibold text-teal-700 bg-teal-100 px-2 py-0.5 rounded-full whitespace-nowrap">Part I — 2 pts</span>
                  <span className="text-xs font-semibold text-gray-500 bg-white px-2 py-0.5 rounded-full border whitespace-nowrap">{q.topic}</span>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{q.standard}</span>
                </div>
                <p className="text-gray-900 font-semibold leading-relaxed text-sm">{q.question}</p>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {q.options.map((opt, oIndex) => {
                  const isSelected = selected === oIndex;
                  const isCorrectOpt = oIndex === q.correctAnswer;
                  let btnClass = 'border-2 border-gray-200 bg-white text-gray-700 hover:border-teal-300 hover:bg-teal-50';
                  if (submitted) {
                    if (isCorrectOpt) btnClass = 'border-2 border-emerald-500 bg-emerald-50 text-emerald-800';
                    else if (isSelected && !isCorrectOpt) btnClass = 'border-2 border-red-400 bg-red-50 text-red-800';
                    else btnClass = 'border-2 border-gray-100 bg-gray-50 text-gray-500';
                  } else if (isSelected) {
                    btnClass = 'border-2 border-teal-500 bg-teal-50 text-teal-800';
                  }
                  return (
                    <button
                      key={oIndex}
                      onClick={() => onSelect(q.id, oIndex)}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all text-sm font-medium cursor-pointer ${btnClass}`}
                    >
                      <span className="font-bold mr-2">{String.fromCharCode(65 + oIndex)}.</span>
                      {opt}
                      {submitted && isCorrectOpt && <i className="ri-check-line ml-2 text-emerald-600" />}
                      {submitted && isSelected && !isCorrectOpt && <i className="ri-close-line ml-2 text-red-500" />}
                    </button>
                  );
                })}
              </div>

              <div className="mt-4">
                <button
                  onClick={() => toggleSolution(q.id)}
                  className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                    showSol ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700'
                  }`}
                >
                  <i className={`${showSol ? 'ri-eye-off-line' : 'ri-eye-line'} mr-1`} />
                  {showSol ? 'Hide Solution' : 'Show Solution'}
                </button>
              </div>

              {showSol && (
                <div className="mt-4 bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-star-fill text-amber-500" />
                    <span className="font-bold text-amber-800 text-sm">Step-by-Step Solution</span>
                  </div>
                  <ol className="space-y-2">
                    {q.solution.map((step, sIndex) => (
                      <li key={sIndex} className="flex items-start gap-3">
                        <span className="w-6 h-6 flex items-center justify-center bg-amber-500 text-white rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                          {sIndex + 1}
                        </span>
                        <p className="text-gray-800 text-sm leading-relaxed">{step}</p>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
